<template>
  <div class="p-6" data-private>
    <!-- Header -->
    <div class="flex items-start justify-between mb-5">
      <div>
        <h2 class="text-base font-semibold text-gray-900">Contract Performance Trackers</h2>
        <p class="text-xs text-gray-500 mt-0.5 max-w-2xl">
          Each contract is scored and displayed in its own methodology — stars, points,
          min/max, benchmarks, or PMPM — driven entirely by the contract's scoring config.
        </p>
      </div>
      <div v-if="!loading && trackers.length" class="flex items-center gap-4">
        <div class="text-right">
          <div class="text-xs text-gray-500">Contracts scored</div>
          <div class="text-sm font-semibold text-gray-800">{{ scoredCount }}</div>
        </div>
        <button
          class="text-xs bg-[#4573D2] hover:bg-[#3a62b8] text-white font-medium rounded
                 px-3 py-2 disabled:opacity-50 whitespace-nowrap"
          :disabled="exporting || !scoredCount"
          :title="`Export ${scoredCount} scored contracts to PowerPoint`"
          @click="exportPptx"
        >
          <font-awesome-icon
            :icon="['fas', exporting ? 'spinner' : 'file-powerpoint']"
            :spin="exporting"
            class="mr-1"
          />
          {{ exporting ? 'Building…' : 'PowerPoint' }}
        </button>
      </div>
    </div>

    <div v-if="loading" class="flex justify-center py-16">
      <font-awesome-icon :icon="['fas', 'spinner']" spin class="text-gray-300 text-xl" />
    </div>

    <div v-else-if="trackers.length === 0" class="text-center py-16 text-sm text-gray-400">
      No contracts with scoring configuration.
    </div>

    <div v-else class="grid grid-cols-1 lg:grid-cols-12 gap-5">
      <!-- Left: single container, grouped by payer, internal vertical scroll -->
      <div class="lg:col-span-4">
        <div class="rounded border border-gray-200 bg-white shadow-sm overflow-y-auto"
             style="max-height: 730px">
          <div v-for="(group, gi) in groupedTrackers" :key="group.payer">
            <!-- payer section header (sticky within scroll) -->
            <div class="sticky top-0 z-10 flex items-center justify-between px-3 py-1.5 bg-gray-50
                        border-b border-gray-100"
                 :class="gi > 0 ? 'border-t border-gray-100' : ''">
              <span class="sensitive text-[10px] font-semibold uppercase tracking-wide text-gray-500">
                {{ group.payer }}
              </span>
              <span class="text-[10px] text-gray-400">{{ group.items.length }}</span>
            </div>
            <!-- contract rows -->
            <button
              v-for="t in group.items"
              :key="t.cpy_entity_id"
              class="w-full text-left px-3 py-2 border-b border-gray-50 last:border-0 transition
                     focus:outline-none focus:ring-1 focus:ring-inset focus:ring-[#4573D2]"
              :class="selectedId === t.cpy_entity_id
                ? 'bg-primary-50'
                : 'hover:bg-gray-50'"
              @click="select(t.cpy_entity_id)"
            >
              <div class="flex items-center justify-between gap-3">
                <div class="min-w-0 flex items-center gap-2">
                  <span class="w-1 h-8 rounded-full shrink-0"
                        :class="selectedId === t.cpy_entity_id ? 'bg-primary-500' : 'bg-transparent'"></span>
                  <div class="min-w-0">
                    <div class="sensitive text-xs font-medium text-gray-900">{{ lobName(t) }}</div>
                    <div class="mt-0.5 flex items-center gap-1.5">
                      <span class="inline-block text-[9px] font-medium uppercase tracking-wide
                                   px-1 py-0.5 rounded" :class="methodChip(t.methodology)">
                        {{ methodologyLabel(t.methodology) }}
                      </span>
                      <span v-if="!t.scoring_unavailable_reason" class="text-[9px] text-gray-400">{{ t.measure_count }} meas</span>
                    </div>
                  </div>
                </div>
                <div class="text-right shrink-0">
                  <div class="text-xs font-semibold" :class="t.savings_eligible ? 'text-green-700' : 'text-gray-800'">
                    {{ t.label }}
                  </div>
                  <div v-if="t.projected_label" class="text-[9px] text-[#4573D2]">
                    → {{ t.projected_label }}
                  </div>
                </div>
              </div>
            </button>
          </div>
        </div>
      </div>

      <!-- Right: the config-driven tracker -->
      <div class="lg:col-span-8">
        <div class="rounded border border-gray-200 bg-white shadow-sm min-h-[400px]">
          <div v-if="trackerLoading" class="flex justify-center py-24">
            <font-awesome-icon :icon="['fas', 'spinner']" spin class="text-gray-300 text-xl" />
          </div>
          <div v-else-if="activeSpec?.scoring_unavailable_reason" class="p-5">
            <h3 class="sensitive text-sm font-semibold text-gray-900">{{ activeSpec.cpy_name }}</h3>
            <p class="text-sm font-medium text-gray-700 mt-4">Score unavailable</p>
            <p class="sensitive text-sm text-gray-600 mt-2" data-demo-blur>{{ activeSpec.scoring_unavailable_reason }}</p>
            <p class="text-xs text-gray-500 mt-4">Open gaps remain available in the Quality worklist. No score, forecast, or scored export is produced from this source.</p>
          </div>
          <Contract_Tracker
            v-else-if="activeSpec"
            :spec="activeSpec"
            :result="activeResult"
          />
          <div v-else class="flex items-center justify-center py-24 text-sm text-gray-400">
            Select a contract to see its tracker.
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Contract_Tracker from '@/components/Quality/Contract_Tracker.vue';
import { exportBlockedInDemoMode } from '@/utils/demoSpoof';

export default {
  name: 'Quality_ContractTrackers',
  components: { Contract_Tracker },
  data() {
    return {
      loading: false,
      trackerLoading: false,
      trackers: [],
      methodologies: {},
      selectedId: null,
      activeSpec: null,
      activeResult: null,
      exporting: false,
    };
  },
  async mounted() {
    await this.loadMethodologies();
    await this.loadTrackers();
  },
  computed: {
    scoredCount() {
      return this.trackers.filter(t => !t.scoring_unavailable_reason).length;
    },
    groupedTrackers() {
      const groups = {};
      this.trackers.forEach((t) => {
        const p = this.payerOf(t);
        (groups[p] = groups[p] || []).push(t);
      });
      // sort payers alpha, contracts within by score desc
      return Object.keys(groups).sort().map((payer) => ({
        payer,
        items: groups[payer].slice().sort((a, b) => (b.score || 0) - (a.score || 0)),
      }));
    },
  },
  methods: {
    // Export the FULL deck — cover + all-contracts roll-up + one slide per
    // contract, for every contract on the tab. Never scoped to the selected
    // contract (Jen/Irene 2026-07-30: individual contract slides AND a roll-up
    // of all of them, in one deck).
    async exportPptx() {
      if (exportBlockedInDemoMode() || !this.scoredCount) return;
      this.exporting = true;
      try {
        const res = await this.$axios.post(
          '/api/stars-performance/trackers/export-pptx/', {},
          { responseType: 'blob' },
        );
        let fname = 'Contract_Quality_Performance.pptx';
        const cd = res.headers && (res.headers['content-disposition']
          || (res.headers.get && res.headers.get('content-disposition')));
        if (cd) {
          const m = /filename="?([^"]+)"?/.exec(cd);
          if (m) [, fname] = m;
        }
        const url = window.URL.createObjectURL(new Blob([res.data]));
        const a = document.createElement('a');
        a.href = url;
        a.download = fname;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
      } catch (e) {
        this.$toast?.error?.('Could not build the PowerPoint export.');
      } finally {
        this.exporting = false;
      }
    },
    payerOf(t) {
      const name = t.cpy_entity_name || t.contract_id || '';
      const known = ['Aetna', 'Anthem', 'Cigna', 'EmblemHealth', 'Emblem', 'Fidelis',
        'Humana', 'Oxford', 'UnitedHealthcare', 'UHC', 'BCBS', 'HealthFirst',
        'New York State Health Insurance Program', 'NYSHIP'];
      const hit = known.find((k) => name.toLowerCase().includes(k.toLowerCase()));
      if (!hit) return 'Other';
      // normalize aliases to one payer label
      if (hit === 'Emblem') return 'EmblemHealth';
      if (hit === 'UHC') return 'UnitedHealthcare';
      if (hit === 'New York State Health Insurance Program') return 'NYSHIP';
      return hit;
    },
    lobName(t) {
      // strip the payer prefix + trailing year → just the LOB (e.g. "Medicare Advantage New York")
      const payer = this.payerOf(t);
      let name = (t.cpy_entity_name || t.contract_id || '').replace(/\s*20\d\d$/, '');
      // remove leading payer token(s)
      const aliases = { EmblemHealth: 'EmblemHealth', UnitedHealthcare: 'UnitedHealthcare',
        NYSHIP: 'New York State Health Insurance Program' };
      const prefix = aliases[payer] || payer;
      const re = new RegExp('^' + prefix.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') + '\\s*', 'i');
      name = name.replace(re, '').trim();
      return name || (t.cpy_entity_name || t.contract_id);
    },
    async loadMethodologies() {
      try {
        const res = await this.$axios.get('/api/stars-performance/methodologies/');
        (res.data.methodologies || []).forEach((m) => { this.methodologies[m.key] = m; });
      } catch (e) { /* non-fatal */ }
    },
    methodologyLabel(key) {
      const m = this.methodologies[key];
      if (m) return m.name.replace(/\s*\(.*\)/, '');
      return (key || '').replace(/_/g, ' ');
    },
    methodChip(key) {
      // subtle, methodology-family tint (neutral by default)
      const map = {
        ma_stars: 'bg-amber-50 text-amber-700',
        min_max_points: 'bg-primary-100 text-primary-700',
        min_max_pmpm: 'bg-green-50 text-green-700',
        benchmark_any: 'bg-neutral-100 text-neutral-800',
        medicaid_tiers: 'bg-violet-50 text-violet-700',
        single_goal: 'bg-gray-100 text-gray-600',
      };
      return map[key] || 'bg-gray-100 text-gray-600';
    },
    contractShortName(t) {
      return (t.cpy_entity_name || t.contract_id || '').replace(/\s*20\d\d$/, '');
    },
    async loadTrackers() {
      this.loading = true;
      try {
        const res = await this.$axios.get('/api/stars-performance/trackers/');
        this.trackers = res.data.trackers || [];
        if (this.trackers.length) this.select(this.trackers[0].cpy_entity_id);
      } catch (e) {
        this.trackers = [];
      } finally {
        this.loading = false;
      }
    },
    async select(cpyId) {
      this.selectedId = cpyId;
      this.trackerLoading = true;
      this.activeSpec = null;
      this.activeResult = null;
      try {
        const res = await this.$axios.get(`/api/stars-performance/tracker/${cpyId}/`);
        this.activeSpec = res.data.spec;
        this.activeResult = res.data.result;
      } catch (e) {
        this.activeSpec = null;
      } finally {
        this.trackerLoading = false;
      }
    },
  },
};
</script>
